/**
 * Environment variable utility for Next.js App Router
 *
 * Provides type-safe access to environment variables
 */

import { publicEnv } from '@/lib/config/public-env';
import { tracking } from '@/lib/features/tracking';

// Define types for environment variables
interface EnvVariables {
    // Common environment variables
    NEXT_PUBLIC_BUILD_ID: string;
    NEXT_PUBLIC_APP_VERSION: string;
    NEXT_PUBLIC_APP_ENV: 'test1' | 'test2' | 'staging' | 'production';
    NEXT_PUBLIC_COOKIE_DOMAIN: string;

    // API endpoints
    NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT: string;
    NEXT_PUBLIC_PLATFORM_API_BASE_ENDPOINT: string;
    NEXT_PUBLIC_SALESFORCE_API_BASE_ENDPOINT: string;

    // URLs
    NEXT_PUBLIC_STUDIO_URL: string;
}

/**
 * Get an environment variable with type safety
 */
const warnedMissingEnvKeys = new Set<string>();

export const getEnv = <K extends keyof EnvVariables>(key: K): EnvVariables[K] => {
    const value = publicEnv(key);

    if (value === undefined) {
        // Warn once per key — missing APP_ENV during a render loop otherwise floods tracking.
        if (!warnedMissingEnvKeys.has(key as string)) {
            warnedMissingEnvKeys.add(key as string);
            tracking.errorWithSource(
                new Error(`Environment variable ${key} is not defined`),
                'lib.config.env',
            );
        }
        return '' as EnvVariables[K];
    }

    return value as EnvVariables[K];
};

/**
 * Get the current environment (test1, test2, staging, production)
 */
export const getEnvironment = (): EnvVariables['NEXT_PUBLIC_APP_ENV'] => {
    return getEnv('NEXT_PUBLIC_APP_ENV');
};

/**
 * Check if the current environment is a production environment
 */
export const isProduction = (): boolean => {
    return getEnvironment() === 'production';
};

/**
 * Check if the current environment is a staging environment
 */
export const isStaging = (): boolean => {
    return getEnvironment() === 'staging';
};

/**
 * Check if the current environment is a test environment
 */
export const isTest = (): boolean => {
    const env = getEnvironment();
    return env === 'test1' || env === 'test2';
};
